import { apiService } from '@/services/api';
import { calcularHRN } from '../utils/hrn-calculator';
import { Risco } from '../types';

export function useIAAnalysis(
  /** `t` do `useI18n()` do componente — os avisos precisam reagir à troca de idioma. */
  t: (chave: string, variaveis?: Record<string, string | number>) => string,
  fotoParte: string,
  descricaoMaquinaIA: string,
  nomeParte: string,
  parte: string,
  descricaoCondicaoAtual: string[],
  perigosEncontrados: string[],
  potenciaisConsequencias: string[],
  riscosEditados: Risco[],
  setNomeParte: (nome: string) => void,
  setCiclosVida: (ciclos: string[]) => void,
  setEventoPerigoso: (evento: string) => void,
  setTiposPerigoSelecionados: (tipos: string[]) => void,
  setPotenciaisConsequencias: (consequencias: string[]) => void,
  setPerigosEncontrados: (perigos: string[]) => void,
  setConforme: (conforme: boolean) => void,
  setDescricaoCondicaoAtual: (descricao: string[]) => void,
  setMedidasControle: (medidas: string[]) => void,
  setExigenciasNormativas: (exigencias: string[]) => void,
  setAdequacoesNormativas: (adequacoes: string[]) => void,
  setRiscosEditados: (riscos: Risco[]) => void,
  setMostrarDialogIA: (mostrar: boolean) => void,
  setAnalisandoIA: (analisando: boolean) => void
) {
  const handleAnalisarComIA = async () => {
    if (!fotoParte) {
      alert(t('apreciacaoModais.editarParte.ia.erroSemImagem'));
      return;
    }

    if (!descricaoMaquinaIA || descricaoMaquinaIA.trim() === '') {
      alert(t('apreciacaoModais.editarParte.ia.erroSemDescricao'));
      return;
    }

    setAnalisandoIA(true);
    try {
      // Preparar descrição da condição atual
      const descricaoCondicaoAtualStr = descricaoCondicaoAtual.length > 0 
        ? descricaoCondicaoAtual.join('\n') 
        : undefined;
      
      // Preparar perigos mecânicos (filtrar apenas os que são realmente mecânicos)
      const perigosMecanicosList = perigosEncontrados.filter(p => {
        const pLower = p.toLowerCase();
        return !pLower.includes('elétrico') && !pLower.includes('eletrico') && 
               !pLower.includes('ergonômico') && !pLower.includes('ergonomico');
      });
      
      const response = await apiService.analisarRiscosIA({
        imagemBase64: fotoParte,
        descricaoMaquina: descricaoMaquinaIA,
        parte: nomeParte || parte,
        descricaoCondicaoAtual: descricaoCondicaoAtualStr,
        perigosMecanicos: perigosMecanicosList.length > 0 ? perigosMecanicosList : undefined,
        consequencias: potenciaisConsequencias.length > 0 ? potenciaisConsequencias : undefined,
      });

      if (response.success && response.data?.riscos && response.data.riscos.length > 0) {
        const primeiroRisco = response.data.riscos[0];
        
        // Preencher campos automaticamente
        if (primeiroRisco.parte && !nomeParte) {
          setNomeParte(primeiroRisco.parte);
        }
        
        if (primeiroRisco.cicloVida) {
          const ciclos = primeiroRisco.cicloVida.split(',').map((c: string) => c.trim()).filter((c: string) => c);
          setCiclosVida(ciclos);
        }
        
        if (primeiroRisco.eventoPerigoso) {
          setEventoPerigoso(primeiroRisco.eventoPerigoso);
        }
        
        if (primeiroRisco.tipoPerigo) {
          const tipos = primeiroRisco.tipoPerigo.split(',').map((t: string) => t.trim()).filter((t: string) => t);
          setTiposPerigoSelecionados(tipos);
        }
        
        if (primeiroRisco.consequencias) {
          // Pode vir como string separada por vírgula ou já como array
          let consequencias: string[] = [];
          if (Array.isArray(primeiroRisco.consequencias)) {
            consequencias = primeiroRisco.consequencias;
          } else {
            consequencias = primeiroRisco.consequencias.split(',').map((c: string) => c.trim()).filter((c: string) => c);
          }
          setPotenciaisConsequencias(consequencias);
        }
        
        // Processar perigos encontrados
        if (primeiroRisco.perigosEncontrados) {
          let perigos: string[] = [];
          if (Array.isArray(primeiroRisco.perigosEncontrados)) {
            perigos = primeiroRisco.perigosEncontrados;
          } else if (typeof primeiroRisco.perigosEncontrados === 'string') {
            perigos = primeiroRisco.perigosEncontrados.split(',').map((p: string) => p.trim()).filter((p: string) => p);
          }
          if (perigos.length > 0) {
            setPerigosEncontrados(perigos);
          }
        }
        
        // Processar conformeAtual
        if (primeiroRisco.conformeAtual !== undefined) {
          setConforme(primeiroRisco.conformeAtual);
        }
        
        if (primeiroRisco.descricaoCondicaoAtual) {
          const descricoes = primeiroRisco.descricaoCondicaoAtual.split('\n').filter((d: string) => d.trim());
          setDescricaoCondicaoAtual(descricoes);
        }
        
        if (primeiroRisco.medidasControle) {
          const medidas = primeiroRisco.medidasControle.split('\n').filter((m: string) => m.trim());
          setMedidasControle(medidas);
        }
        
        if (primeiroRisco.exigenciasNormativas) {
          const exigencias = primeiroRisco.exigenciasNormativas.split('\n').filter((e: string) => e.trim());
          setExigenciasNormativas(exigencias);
        }
        
        if (primeiroRisco.adequacoesNormativas) {
          const adequacoes = primeiroRisco.adequacoesNormativas.split('\n').filter((a: string) => a.trim());
          setAdequacoesNormativas(adequacoes);
        }

        // Atualizar riscos editados se houver
        if (riscosEditados.length > 0) {
          const novosRiscos = [...riscosEditados];
          const riscoAtualizado = {
            ...novosRiscos[0],
            tipoPerigo: primeiroRisco.tipoPerigo || novosRiscos[0].tipoPerigo,
            gravidadeAtual: primeiroRisco.gravidadeAtual || novosRiscos[0].gravidadeAtual,
            frequenciaAtual: primeiroRisco.frequenciaAtual || novosRiscos[0].frequenciaAtual,
            exposicaoAtual: primeiroRisco.exposicaoAtual || novosRiscos[0].exposicaoAtual,
            probabilidadeAtual: primeiroRisco.probabilidadeAtual || novosRiscos[0].probabilidadeAtual,
            eventoPerigoso: primeiroRisco.eventoPerigoso || novosRiscos[0].eventoPerigoso,
            consequencias: primeiroRisco.consequencias || novosRiscos[0].consequencias,
            cicloVida: primeiroRisco.cicloVida || novosRiscos[0].cicloVida,
            adequacoesTecnicas: primeiroRisco.descricaoCondicaoAtual || novosRiscos[0].adequacoesTecnicas,
            medidasControle: primeiroRisco.medidasControle || novosRiscos[0].medidasControle,
            exigenciasNormativas: primeiroRisco.exigenciasNormativas || novosRiscos[0].exigenciasNormativas,
            adequacoesNormativas: primeiroRisco.adequacoesNormativas || novosRiscos[0].adequacoesNormativas,
          };
          
          // Recalcular HRN sempre com os valores atuais
          // Fórmula: HRN = PO × FE × GS × NP
          const po = (riscoAtualizado.probabilidadeAtual != null && !isNaN(Number(riscoAtualizado.probabilidadeAtual)) && Number(riscoAtualizado.probabilidadeAtual) > 0) ? Number(riscoAtualizado.probabilidadeAtual) : 1;
          const fe = (riscoAtualizado.frequenciaAtual != null && !isNaN(Number(riscoAtualizado.frequenciaAtual)) && Number(riscoAtualizado.frequenciaAtual) > 0) ? Number(riscoAtualizado.frequenciaAtual) : 1;
          const gs = (riscoAtualizado.gravidadeAtual != null && !isNaN(Number(riscoAtualizado.gravidadeAtual)) && Number(riscoAtualizado.gravidadeAtual) > 0) ? Number(riscoAtualizado.gravidadeAtual) : 1;
          const np = (riscoAtualizado.exposicaoAtual != null && !isNaN(Number(riscoAtualizado.exposicaoAtual)) && Number(riscoAtualizado.exposicaoAtual) > 0) ? Number(riscoAtualizado.exposicaoAtual) : 1;
          riscoAtualizado.hrnAtual = calcularHRN(po, fe, gs, np);
          
          novosRiscos[0] = riscoAtualizado;
          setRiscosEditados(novosRiscos);
        }

        setMostrarDialogIA(false);
        alert(t('apreciacaoModais.editarParte.ia.concluida', { total: response.data.riscos.length }));
      } else {
        alert(response.message || t('apreciacaoModais.editarParte.ia.nenhumRisco'));
      }
    } catch (error: any) {
      console.error('Erro ao analisar com IA:', error);
      alert(t('apreciacaoModais.editarParte.ia.erroAnalisar', { mensagem: error.message || t('apreciacaoModais.editarParte.ia.erroDesconhecido') }));
    } finally {
      setAnalisandoIA(false);
    }
  };

  return {
    handleAnalisarComIA,
  };
}

